import { init, loadRemote } from "@module-federation/runtime";
import { getLocationSettings } from "@glide/common-core/dist/js/location";
import React from "react";
import ReactDOM from "react-dom";
import * as jsxRuntime from "react/jsx-runtime";
import * as jsxDevRuntime from "react/jsx-dev-runtime";
import { ErrorBoundary } from "@glide/common";

interface Props {
    readonly loading: React.ReactNode;
    readonly error: React.ReactNode;
    readonly children: React.ReactNode;
}

export const SuspenseAndErrorFallback: React.FC<Props> = p => {
    const { loading, error, children } = p;

    return (
        <ErrorBoundary fallback={error}>
            <React.Suspense fallback={loading}>{children}</React.Suspense>
        </ErrorBoundary>
    );
};

export class RemoteModuleLoader {
    private static instance: RemoteModuleLoader | undefined;

    static get(): RemoteModuleLoader {
        if (this.instance === undefined) {
            init({
                name: "remote-loading-container",
                remotes: [
                    {
                        name: "example",
                        entry:
                            process.env.NODE_ENV === "development"
                                ? "http://localhost:4000/remoteEntry.js"
                                : `${getLocationSettings().remoteModulesGCSContainer}example/latest/remoteEntry.js`,
                    },
                ],
                shared: {
                    react: {
                        version: React.version,
                        lib: () => React,
                        shareConfig: { singleton: true, requiredVersion: React.version },
                    },
                    "react-dom": {
                        version: ReactDOM.version,
                        lib: () => ReactDOM,
                        shareConfig: { singleton: true, requiredVersion: ReactDOM.version },
                    },
                    "react/jsx-runtime": {
                        version: React.version,
                        lib: () => jsxRuntime,
                        shareConfig: { singleton: true, requiredVersion: React.version },
                    },
                    "react/jsx-dev-runtime": {
                        version: React.version,
                        lib: () => jsxDevRuntime,
                        shareConfig: { singleton: true, requiredVersion: React.version },
                    },
                },
            });
            this.instance = new RemoteModuleLoader();
        }

        return this.instance;
    }

    public loadRemoteComponent = (remoteName: string, componentPath: string) => {
        return React.lazy(async () => {
            const [_, module] = await Promise.all([
                // Loads the styles for the remote component.
                // this is cached by module-federation/runtime so we can safely call it here multiple times
                // we may opt to modify the styles approach anyway TBD
                loadRemote(`${remoteName}/styles`),
                loadRemote(`${remoteName}/${componentPath}`),
            ]);
            if (module && typeof module === "object" && "default" in module) {
                return { default: (module as { default: React.ComponentType<any> }).default };
            }
            throw new Error(`Invalid module format for ${remoteName}/${componentPath}`);
        });
    };
}
